import { useEffect } from 'react';
import { useCalibrationV2Store } from '../store/calibrationV2';
import { apiV2 } from '../client/apiV2';

/** Serialize live writes and cancel work from the previous device/editor session. */
export function useLiveWarpSyncV2(): void {
  const deviceId = useCalibrationV2Store(s => s.deviceId);
  const sessionVersion = useCalibrationV2Store(s => s.sessionVersion);
  const connectionStatus = useCalibrationV2Store(s => s.connectionStatus);
  const ready = useCalibrationV2Store(s => s.liveWarpSyncReady);
  useEffect(() => {
    if (!deviceId || !ready || connectionStatus !== 'online') return;
    const controller = new AbortController();
    let frame: number | null = null;
    let retry: ReturnType<typeof setTimeout> | null = null;
    let pending = false;
    let lastSent = '';
    const current = () => !controller.signal.aborted && useCalibrationV2Store.getState().sessionVersion === sessionVersion &&
      useCalibrationV2Store.getState().liveWarpSyncReady;
    const schedule = () => {
      if (!current() || pending || frame !== null || retry !== null) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        if (!current()) return;
        const { surfaces, bypassWarp } = useCalibrationV2Store.getState();
        const payload = { surfaces, bypassWarp };
        const key = JSON.stringify(payload);
        if (key === lastSent) return;
        pending = true;
        void apiV2.liveWarp(deviceId, payload, controller.signal).then(() => {
          if (current()) lastSent = key;
        }).catch(() => {
          if (current()) retry = setTimeout(() => { retry = null; schedule(); }, 500);
        }).finally(() => { pending = false; schedule(); });
      });
    };
    const unsubscribe = useCalibrationV2Store.subscribe((next, previous) => {
      if (next.surfaces !== previous.surfaces || next.bypassWarp !== previous.bypassWarp) schedule();
    });
    schedule();
    return () => {
      controller.abort(); unsubscribe();
      if (frame !== null) cancelAnimationFrame(frame);
      if (retry !== null) clearTimeout(retry);
    };
  }, [deviceId, sessionVersion, connectionStatus, ready]);
}
